/**
 * When a downward drag on a bottom sheet counts as "put it away".
 *
 * Two ways to mean it, because people do both: drag it most of the way down, or flick it. A flick
 * is short and fast and never reaches the threshold, so distance alone would ignore it and the
 * sheet would spring back in the hand of somebody who did the most natural gesture there is.
 *
 * Upward drags are not dismissals at any speed — the sheet is already at the bottom of the screen
 * and there is nowhere up to go.
 */
export const DISMISS_PX = 64;
export const FLICK_PX = 24;
export const FLICK_VELOCITY = 0.5;   // px per ms

export function shouldDismiss(dy: number, dtMs: number): boolean {
  if (dy <= 0) return false;
  if (dy >= DISMISS_PX) return true;
  if (dtMs <= 0) return false;
  return dy >= FLICK_PX && dy / dtMs >= FLICK_VELOCITY;
}

/**
 * How far the sheet follows the finger.
 *
 * Down, one for one: the sheet is going where the finger goes. Up, a rubber band: the sheet is
 * already at the bottom of the screen and has nowhere to go, but a sheet that does not move at all
 * under a finger reads as stuck, so it stretches a little and less the further it is pulled, and
 * snaps back on release. Tim, 4 September: "a little elasticity in pull up / swipe down".
 * The asymptote is `STRETCH_PX`; a 40px pull gives about 16px, a hard yank never more than 28.
 */
export const STRETCH_PX = 28;

export function followY(dy: number): number {
  if (dy >= 0) return dy;
  const d = -dy;
  return -(STRETCH_PX * d) / (d + STRETCH_PX);
}

/**
 * Whether a drag that began on this element belongs to something inside the sheet.
 *
 * The sheet follows any downward drag and lets go past `DISMISS_PX`; a list inside it that
 * scrolls on its own (Act as another GM, ten rows in a 260px box) needs the same gesture to move
 * the list. Tim, 4 September: the menu closed while he scrolled that list. So a drag that starts
 * inside a scrolling element is the element's, and the sheet stays where it is. Pure, with the
 * measurements handed in, so the rule is testable without a browser.
 */
export function ownsDrag(
  path: ReadonlyArray<{ overflowY: string; scrollHeight: number; clientHeight: number; scrollTop?: number }>,
): boolean {
  return path.some((el) => /(auto|scroll)/.test(el.overflowY)
    && el.scrollHeight > el.clientHeight
    /*
     * A list scrolled to its top has nothing above to show, so a downward drag there is the
     * sheet's again: the way every phone's own sheets behave. `scrollTop` is optional so the
     * earlier callers, which measured no scroll position, keep the stricter rule.
     */
    && (el.scrollTop === undefined || el.scrollTop > 0));
}
